import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';
import { BillingCycleTab, CALL_MARKS, markMetaFor } from './BillingCycleTab';
import { ReviewOrderDialog } from './ordersTab/ReviewOrderDialog';
import { useWindowedList } from '../../hooks/useWindowedList';

// ============ DEALER CRM TAB ============
export const DealerCRMTab = ({ userRole, refreshKey = 0, products = [] }) => {
  const confirm = useConfirm();
  const [activeSubTab, setActiveSubTab] = useState('crm'); // 'crm' | 'billing'
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('outstanding'); // 'outstanding' | 'last_dispatch' | 'dealer_name'
  const [sortDir, setSortDir] = useState('desc');
  const [detail, setDetail] = useState(null); // { dealer, orders, outstanding_invoices }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSortDir, setDetailSortDir] = useState('desc');
  const [bulkQueue, setBulkQueue] = useState(null); // { items: [rows...], sent: Set<id> }
  // Order review dialog — stacked ON TOP of the Dealer Detail dialog so the
  // operator can drill into an order and pop back to the dispatch list with a
  // single close (user spec: "always de-escalate one step only").
  const [reviewOrder, setReviewOrder] = useState(null);
  const [agingBucket, setAgingBucket] = useState(null); // null | 'critical' | 'warning' | 'caution'
  const [markFilter, setMarkFilter] = useState([]); // [] = any; multi-select of CALL_MARKS keys

  // Tracks whether a load() call is currently in flight. Prevents the silent
  // refresh effect (refreshKey bump on tab re-entry) from firing a SECOND
  // concurrent load while the first one is still racing the PDF-upload
  // wipe-and-insert in the backend — which would produce duplicate
  // "Failed to load CRM data" toasts mid-upload.
  const loadInFlightRef = React.useRef(false);
  const load = async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoading(true);
    try {
      const [rowsRes, metaRes] = await Promise.all([crmAPI.getDealers(), crmAPI.getMeta()]);
      setRows(rowsRes.data);
      setMeta(metaRes.data);
    } catch (e) {
      // Suppress the toast while a PDF upload is in flight — the backend
      // briefly tears down + rebuilds the outstandings collection during
      // upload (wipe-on-upload), so transient read failures are expected
      // and would otherwise spam the operator with 2-3 false "Failed to
      // load" errors before the post-upload load() succeeds.
      if (!uploading) {
        toast.error('Failed to load CRM data', { id: 'crm-load-error' });
      }
    } finally {
      setLoading(false);
      loadInFlightRef.current = false;
    }
  };
  useEffect(() => { load(); }, []);

  // Silent refresh on tab re-entry: parent bumps `refreshKey` and we re-pull
  // the CRM dataset without resetting the search input, sort, aging filter,
  // or the open detail dialog. Skips the initial render via a useRef sentinel
  // because the mount effect above already covers the first load.
  const isFirstCrmRefreshRef = React.useRef(true);
  useEffect(() => {
    if (isFirstCrmRefreshRef.current) { isFirstCrmRefreshRef.current = false; return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleUpload = async (e) => {
    const f = e.target.files[0];
    if (!f) { return; }
    // Warn user that all previous outstandings will be replaced
    const hasExisting = meta?.dealer_blocks && meta.dealer_blocks > 0;
    if (hasExisting) {
      const ok = await confirm({
        title: 'Replace existing CRM data?',
        description: `This will delete all ${meta.dealer_blocks} existing dealer outstanding records and replace them with the new PDF.`,
        confirmLabel: 'Replace',
        destructive: true,
      });
      if (!ok) { e.target.value = ''; return; }
    }
    setUploading(true);
    try {
      const res = await crmAPI.uploadOutstandings(f);
      toast.success(res.data.message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleEraseAll = async () => {
    const ok = await confirm({
      title: 'Erase ALL CRM outstanding records?',
      description: 'This will HARD-DELETE every CRM outstanding record from the database (including any old data uploaded before the wipe-on-upload feature was deployed). This cannot be undone.',
      confirmLabel: 'Erase all',
      destructive: true,
    });
    if (!ok) return;
    setErasing(true);
    try {
      const res = await crmAPI.eraseAllOutstandings();
      const out = res.data?.outstandings_deleted ?? 0;
      const meta = res.data?.meta_deleted ?? 0;
      toast.success(`Erased ${out} outstanding row(s) and ${meta} meta record(s).`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erase failed');
    } finally {
      setErasing(false);
    }
  };


  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '-'; }
  };

  // Aging buckets: only count dealers with outstanding > 0.
  // Declared BEFORE `filtered` so the aging-chip click filter can call it
  // without hitting a temporal-dead-zone ReferenceError (bug 20/Feb/2026 —
  // previously declared further down the component body, which silently
  // broke the chip filter on click because the `const` was uninitialised
  // at the time `rows.filter(...)` executed).
  const bucketOf = (d) => {
    if (d > 30) return 'critical';
    if (d >= 15) return 'warning';
    if (d >= 1) return 'caution';
    return null;
  };

  const filtered = rows.filter(r => {
    if (overdueOnly && !r.is_overdue) return false;
    if (markFilter.length > 0 && !markFilter.includes(r.crm_call_mark || 'none')) return false;
    if (agingBucket) {
      if (!(r.outstanding_total > 0)) return false;
      if (bucketOf(r.oldest_od_days || 0) !== agingBucket) return false;
    }
    if (search) {
      // Tokenized AND-match: "ravi palsana" finds Ravi in Palsana.
      const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
      if (tokens.length) {
        const hay = `${r.dealer_name || ''} ${r.town || ''} ${r.phone_number || ''}`.toLowerCase();
        if (!tokens.every(t => hay.includes(t))) return false;
      }
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortBy === 'dealer_name') { va = (a.dealer_name || '').toUpperCase(); vb = (b.dealer_name || '').toUpperCase(); }
    else if (sortBy === 'last_dispatch') { va = a.last_dispatch || ''; vb = b.last_dispatch || ''; }
    else if (sortBy === 'outstanding') { va = a.outstanding_total || 0; vb = b.outstanding_total || 0; }
    else if (sortBy === 'oldest_od_days') { va = a.oldest_od_days || 0; vb = b.oldest_od_days || 0; }
    else if (sortBy === 'cycle') { va = a.billing_cycle_days || 0; vb = b.billing_cycle_days || 0; }
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const { visible: visibleSorted, hasMore: crmHasMore, remaining: crmRemaining, loadMoreRef: crmLoadMoreRef, showMore: crmShowMore } =
    useWindowedList(sorted, { resetDeps: [search, overdueOnly, agingBucket, markFilter.join(','), sortBy, sortDir, sorted.length] });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'dealer_name' ? 'asc' : 'desc'); }
  };

  const openDetail = async (row) => {
    setDetail({ _loading: true, dealer: { id: row.id, name: row.dealer_name, town: row.town, phone_number: row.phone_number }, orders: [], outstanding_invoices: [] });
    setDetailSortDir('desc');
    setDetailLoading(true);
    try {
      const res = await crmAPI.getDealerDetail(row.id);
      setDetail(res.data);
    } catch { toast.error('Failed to load dealer detail'); }
    finally { setDetailLoading(false); }
  };

  // ---- Call-mark tagging (reuses Billing Cycle marks) + bulk multi-select ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCallOn, setBulkCallOn] = useState('');
  const [markDraftDate, setMarkDraftDate] = useState({});
  const [exporting, setExporting] = useState(false);

  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selectedIds.has(r.id));
  const toggleSelectAll = () => setSelectedIds(prev => {
    const n = new Set(prev);
    const allSel = filtered.length > 0 && filtered.every(r => n.has(r.id));
    if (allSel) filtered.forEach(r => n.delete(r.id));
    else filtered.forEach(r => n.add(r.id));
    return n;
  });

  const setCallMark = async (row, mark, callOnDate = null) => {
    setRows(prev => prev.map(r => r.id === row.id
      ? { ...r, crm_call_mark: mark, crm_call_on_date: mark === 'call_on' ? (callOnDate || null) : null }
      : r));
    try {
      await crmAPI.setCrmCallMark(row.id, { mark, call_on_date: mark === 'call_on' ? (callOnDate || null) : null, note: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mark');
      load();
    }
  };

  const bulkSetMark = async (mark, callOnDate = null) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (mark === 'call_on' && !callOnDate) { toast.error('Pick a date for "Call on…"'); return; }
    setBulkBusy(true);
    setRows(prev => prev.map(r => ids.includes(r.id)
      ? { ...r, crm_call_mark: mark, crm_call_on_date: mark === 'call_on' ? (callOnDate || null) : null }
      : r));
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await crmAPI.setCrmCallMark(id, { mark, call_on_date: mark === 'call_on' ? (callOnDate || null) : null, note: '' }); ok++; }
      catch (e) { fail++; }
    }
    setBulkBusy(false);
    if (fail === 0) toast.success(`Marked ${ok} dealer${ok > 1 ? 's' : ''}: ${markMetaFor(mark).label}`);
    else toast.error(`${ok} saved, ${fail} failed — refreshing`);
    clearSelection(); setBulkCallOn(''); load();
  };

  const exportCrm = async (fmt) => {
    if (filtered.length === 0) { toast.info('No rows to export'); return; }
    setExporting(true);
    try {
      const payload = {
        title: `Outstanding & Aging${agingBucket ? ` (${agingBucket})` : ''}${overdueOnly ? ' · overdue only' : ''}`,
        rows: sorted.map(r => ({
          dealer_name: r.dealer_name || '', town: r.town || '', phone_number: r.phone_number || '',
          last_dispatch: r.last_dispatch || null, days_since_dispatch: r.days_since_dispatch ?? null,
          billing_cycle_days: r.billing_cycle_days ?? null, outstanding_total: r.outstanding_total || 0,
          oldest_od_days: r.oldest_od_days || 0, call_mark: r.crm_call_mark || 'none',
          call_on_date: r.crm_call_on_date || null, is_overdue: !!r.is_overdue,
        })),
      };
      const res = fmt === 'excel' ? await crmAPI.exportExcel(payload) : await crmAPI.exportPdf(payload);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-outstanding-aging-${new Date().toISOString().slice(0, 10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${sorted.length} rows to ${fmt === 'excel' ? 'Excel' : 'PDF'}`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  // Compact reusable mark chip + popover for a CRM row.
  const MarkChip = ({ row }) => {
    const meta = markMetaFor(row.crm_call_mark);
    const isCallOn = row.crm_call_mark === 'call_on';
    const draftDate = markDraftDate[row.id] !== undefined ? markDraftDate[row.id] : (row.crm_call_on_date || '');
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors"
            style={meta.bg === 'transparent'
              ? { color: '#5C6B61', borderColor: '#E2DFD8', background: 'white' }
              : { color: meta.color, background: meta.bg, borderColor: meta.bg }}
            data-testid="crm-mark-chip"
          >
            {meta.short}{isCallOn && row.crm_call_on_date && <span className="opacity-90">· {row.crm_call_on_date}</span>}
            <span style={{ marginLeft: 2, fontSize: 9, opacity: 0.75 }}>▾</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1" data-testid="crm-mark-popover" onClick={(e) => e.stopPropagation()}>
          {CALL_MARKS.map(cm => cm.key === 'call_on' ? (
            <div key="call_on" className="px-2 py-1.5 border-t mt-1" style={{ borderColor: '#E2DFD8' }}>
              <div className="text-[11px] font-medium mb-1" style={{ color: '#B08800' }}>Call on a date</div>
              <div className="flex gap-1">
                <Input type="date" value={draftDate} onChange={(e) => setMarkDraftDate(d => ({ ...d, [row.id]: e.target.value }))} className="h-8 text-xs" data-testid="crm-mark-callon-date" />
                <Button size="sm" className="h-8 text-white px-2" style={{ background: '#B08800' }} disabled={!draftDate} onClick={() => setCallMark(row, 'call_on', draftDate)} data-testid="crm-mark-callon-apply">Set</Button>
              </div>
            </div>
          ) : (
            <button
              key={cm.key}
              type="button"
              onClick={() => setCallMark(row, cm.key)}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-[#FBF9F6] text-left"
              data-testid={`crm-mark-option-${cm.key}`}
            >
              <span className="inline-flex items-center gap-2 text-xs" style={{ color: cm.bg === 'transparent' ? '#5C6B61' : cm.bg }}>
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: cm.bg === 'transparent' ? '#B9BEB6' : cm.bg }} />
                {cm.label}
              </span>
              {(row.crm_call_mark || 'none') === cm.key && <CheckCircle className="w-3 h-3" style={{ color: '#2B5339' }} />}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    );
  };


  const overdueCount = rows.filter(r => r.is_overdue).length;
  const grandOutstanding = rows.reduce((s, r) => s + (r.outstanding_total || 0), 0);

  // Aging totals (per-bucket count + amount sum). `bucketOf` is hoisted above
  // `filtered` so the on-click filter doesn't crash with a TDZ error.
  const aging = { critical: { n: 0, amt: 0 }, warning: { n: 0, amt: 0 }, caution: { n: 0, amt: 0 } };
  rows.forEach(r => {
    if (!(r.outstanding_total > 0)) return;
    const b = bucketOf(r.oldest_od_days || 0);
    if (!b) return;
    aging[b].n += 1;
    aging[b].amt += r.outstanding_total || 0;
  });
  const fmtCr = (n) => {
    if (n >= 10000000) return `\u20B9${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `\u20B9${(n / 100000).toFixed(2)}L`;
    if (n >= 1000) return `\u20B9${(n / 1000).toFixed(1)}k`;
    return `\u20B9${n.toFixed(0)}`;
  };

  const buildReminderWA = (row, invoices = []) => {
    const raw = row?.phone_number || '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return null;
    const waNumber = digits.startsWith('91') && digits.length >= 12 ? digits : (digits.length === 10 ? `91${digits}` : digits);
    const amt = Number(row.outstanding_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
    const odDays = row.oldest_od_days || 0;
    const overdueLine = odDays > 0
      ? `Your outstanding balance of *\u20B9${amt}* is *${odDays} days overdue*.`
      : `Your current outstanding balance is *\u20B9${amt}*.`;
    const lines = [
      '*Vikas Industries*',
      'Palsana, Rajasthan',
      '',
      `Namaste ${row.dealer_name || ''},`,
      '',
      overdueLine,
    ];
    // Bill/date-wise breakdown
    if (invoices && invoices.length > 0) {
      lines.push('');
      lines.push('*Bill-wise outstanding:*');
      invoices.forEach((inv, i) => {
        const due = Number(inv.due || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
        const od = inv.od_days || 0;
        const odTag = od > 0 ? ` (${od}d OD)` : '';
        lines.push(`${i + 1}. ${inv.date || '-'} | ${inv.txn_no || '-'} | \u20B9${due}${odTag}`);
      });
      const total = invoices.reduce((s, x) => s + Number(x.due || 0), 0);
      lines.push(`*Total: \u20B9${total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}*`);
    }
    lines.push('');
    lines.push('Kindly arrange the payment at your earliest convenience. For any clarification on the statement, please feel free to reach out.');
    lines.push('');
    lines.push('Thank you for your continued business.');
    lines.push('\u2014 Vikas Industries');
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  const sendReminderWA = async (row, e) => {
    e?.stopPropagation?.();
    if (!row?.phone_number) { toast.error('Dealer mobile number not available'); return; }
    // Mobile browsers (iOS Safari, Android Chrome) block window.open() when
    // called after an await — they only allow popups in direct user-gesture
    // chains. So open the window synchronously NOW with a placeholder, then
    // navigate it once the invoice fetch returns.
    //
    // Bug fix 20/Feb/2026: previously called with `noopener,noreferrer` —
    // modern browsers (Chrome, Edge) return `null` from window.open when
    // `noopener` is set as a security feature, so the subsequent
    // `win.location.href = url` was a no-op and the fallback fired
    // `window.location.href = url` on the CURRENT tab, navigating the app
    // away to api.whatsapp.com. Removing the flags makes window.open return
    // a usable window reference; modern browsers still apply default
    // noopener semantics when `target=_blank` is set via Cross-Origin-Opener
    // policies, so security is not regressed.
    const win = window.open('about:blank', '_blank');
    let invoices = [];
    try {
      const res = await crmAPI.getDealerDetail(row.id);
      invoices = res.data?.outstanding_invoices || [];
    } catch (err) {
      // Non-fatal — fall back to aggregate-only message
    }
    const url = buildReminderWA(row, invoices);
    if (!url) {
      toast.error('Dealer mobile number not available');
      try { win && win.close(); } catch (_) { /* mobile may block close too */ }
      return;
    }
    if (win && !win.closed) {
      win.location.href = url;
    } else {
      // Popup was blocked — fall back to opening in a new tab via anchor click
      // so the current app tab is not navigated away.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
  };

  const openBulkReminders = () => {
    // Build the eligible list with the SAME filters that drive the visible
    // table (overdueOnly + agingBucket + search) so the bulk WhatsApp action
    // matches the operator's intent. "Send WA to all 30+d overdue" requires
    // the aging chip + the bulk button to operate on the same dataset.
    const eligible = rows.filter(r => {
      if (!(r.outstanding_total > 0)) return false;
      if (!r.phone_number) return false;
      if (overdueOnly && !r.is_overdue) return false;
      if (agingBucket && bucketOf(r.oldest_od_days || 0) !== agingBucket) return false;
      if (search) {
        const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
        if (tokens.length) {
          const hay = `${r.dealer_name || ''} ${r.town || ''} ${r.phone_number || ''}`.toLowerCase();
          if (!tokens.every(t => hay.includes(t))) return false;
        }
      }
      return true;
    });
    if (eligible.length === 0) { toast.info('No dealers with outstanding & phone numbers match the current filters'); return; }
    // Sort by oldest_od_days desc so you address the worst ones first
    const items = [...eligible].sort((a, b) => (b.oldest_od_days || 0) - (a.oldest_od_days || 0));
    setBulkQueue({ items, sent: new Set() });
  };

  const sendBulkItem = async (row) => {
    // Open synchronously so mobile browsers don't block the popup (see
    // sendReminderWA for the rationale). `noopener,noreferrer` flags
    // omitted on purpose — they make `window.open` return `null` in
    // modern browsers, which causes the fallback below to navigate the
    // current app tab away.
    const win = window.open('about:blank', '_blank');
    let invoices = [];
    try {
      const res = await crmAPI.getDealerDetail(row.id);
      invoices = res.data?.outstanding_invoices || [];
    } catch (err) {
      // Non-fatal — fall back to aggregate-only message
    }
    const url = buildReminderWA(row, invoices);
    if (!url) {
      try { win && win.close(); } catch (_) { /* noop */ }
      return;
    }
    if (win && !win.closed) {
      win.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
    setBulkQueue((q) => q ? { ...q, sent: new Set([...q.sent, row.id]) } : q);
  };

  const dispatchedOrders = (detail?.orders || []).filter(o => o.status === 'dispatched' || o.status === 'delivered');
  // Use the actual dispatch date (earliest dispatched_at across an order's
  // dispatches[]) instead of order.updated_at — the latter gets stamped to the
  // delivery date when the order is marked delivered.
  const dispatchDateOf = (o) => ((o.dispatches || []).map(x => x.dispatched_at).filter(Boolean).sort()[0]) || '';
  const sortedDispatches = [...dispatchedOrders].sort((a, b) => {
    const va = dispatchDateOf(a) || a.updated_at || '';
    const vb = dispatchDateOf(b) || b.updated_at || '';
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return detailSortDir === 'asc' ? cmp : -cmp;
  });
  // Precedence: item.discount_percent → product master → order.discount_percent → 10%.
  // Mirrors `_order_net_total` (backend) used for invoices & WhatsApp.
  const productDiscountByPid = useMemo(() => {
    const m = {};
    (products || []).forEach((p) => { if (p && p.id) m[p.id] = p.discount_percent; });
    return m;
  }, [products]);
  const orderNetAmt = (o) => {
    const orderPct = o.discount_percent == null ? 10 : Number(o.discount_percent);
    let net = 0;
    (o.items || []).forEach((it) => {
      const pct = it.discount_percent != null
        ? Number(it.discount_percent)
        : (productDiscountByPid[it.product_id] != null ? Number(productDiscountByPid[it.product_id]) : orderPct);
      net += Number(it.total || 0) * (1 - pct / 100);
    });
    return net;
  };

  return (
    <div data-testid="dealer-crm-section">
      <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#2B5339' }}>Dealer CRM</h2>

      {/* Sub-tabs: CRM / Billing Cycle */}
      <div className="flex gap-2 mb-5 border-b" style={{ borderColor: '#E2DFD8' }}>
        <button
          type="button"
          onClick={() => setActiveSubTab('crm')}
          className="px-4 py-2 text-sm font-medium relative transition-colors"
          style={{
            color: activeSubTab === 'crm' ? '#2B5339' : '#5C6B61',
            borderBottom: activeSubTab === 'crm' ? '3px solid #2B5339' : '3px solid transparent',
            marginBottom: '-1px',
          }}
          data-testid="crm-subtab-crm"
        >
          Outstanding & Aging
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('billing')}
          className="px-4 py-2 text-sm font-medium relative transition-colors"
          style={{
            color: activeSubTab === 'billing' ? '#2B5339' : '#5C6B61',
            borderBottom: activeSubTab === 'billing' ? '3px solid #2B5339' : '3px solid transparent',
            marginBottom: '-1px',
          }}
          data-testid="crm-subtab-billing"
        >
          Billing Cycle
        </button>
      </div>

      {activeSubTab === 'billing' ? (
        <BillingCycleTab userRole={userRole} onDealerClick={openDetail} />
      ) : (
        <>
      {/* Summary + toolbar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#5C6B61' }}>Dealers</div>
          <div className="text-2xl font-bold">{rows.length}</div>
        </Card>
        <Card className="p-3 cursor-pointer" onClick={() => setOverdueOnly(!overdueOnly)} data-testid="overdue-card">
          <div className="text-xs" style={{ color: '#C05C35' }}>Overdue {overdueOnly && <span className="ml-1 text-[10px]">(filtering)</span>}</div>
          <div className="text-2xl font-bold" style={{ color: '#C05C35' }}>{overdueCount}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#5C6B61' }}>Total Outstanding</div>
          <div className="text-2xl font-bold" style={{ color: '#2B5339' }}>{'\u20B9'}{grandOutstanding.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
          {meta?.pdf_grand_total_due != null && (
            <div className="text-[11px] mt-0.5" style={{ color: '#5C6B61' }} data-testid="crm-pdf-grand-total">
              PDF: {'\u20B9'}{Number(meta.pdf_grand_total_due).toLocaleString('en-IN', { maximumFractionDigits: 0 })} {meta.pdf_grand_total_due_sign || 'Dr'}
              {meta.pdf_grand_total_net != null && (
                <> · Net {'\u20B9'}{Number(meta.pdf_grand_total_net).toLocaleString('en-IN', { maximumFractionDigits: 0 })} {meta.pdf_grand_total_net_sign || 'Dr'}</>
              )}
            </div>
          )}
          {(() => {
            const eligibleCount = rows.filter(r => r.outstanding_total > 0 && r.phone_number).length;
            if (eligibleCount === 0) return null;
            return (
              <button
                onClick={(e) => { e.stopPropagation(); openBulkReminders(); }}
                title={`Send WhatsApp statement to all ${eligibleCount} dealers with outstanding`}
                data-testid="crm-bulk-wa-btn"
                className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded text-xs hover:opacity-80"
                style={{ background: '#25D366', color: 'white' }}
              >
                <MessageCircle className="w-3 h-3" /> WhatsApp All ({eligibleCount})
              </button>
            );
          })()}
        </Card>
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#5C6B61' }}>Last Uploaded</div>
          <div className="text-sm font-medium">{meta?.last_uploaded_at ? fmtDate(meta.last_uploaded_at) : 'Never'}</div>
          {userRole === 'admin' && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <label className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer" style={{ background: '#2B5339', color: 'white' }} data-testid="upload-outstandings-btn">
                <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Upload PDF'}
                <input type="file" accept="application/pdf" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="upload-outstandings-input" />
              </label>
              <button
                type="button"
                onClick={handleEraseAll}
                disabled={erasing}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50"
                style={{ background: 'white', color: '#C05C35', border: '1px solid #C05C35' }}
                title="Hard-wipe ALL CRM outstanding records (regardless of when uploaded)"
                data-testid="erase-all-outstandings-btn"
              >
                <Trash2 className="w-3 h-3" /> {erasing ? 'Erasing...' : 'Erase All'}
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* Aging breakdown chips */}
      {(aging.critical.n + aging.warning.n + aging.caution.n) > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="crm-aging-strip">
          {[
            { key: 'critical', label: '>30d', color: '#C05C35', data: aging.critical },
            { key: 'warning', label: '15\u201330d', color: '#B08800', data: aging.warning },
            { key: 'caution', label: '1\u201314d', color: '#2B5339', data: aging.caution },
          ].map(b => {
            const active = agingBucket === b.key;
            return (
              <button
                key={b.key}
                onClick={() => setAgingBucket(active ? null : b.key)}
                className="px-3 py-1.5 rounded-full text-xs border transition-all"
                style={{
                  background: active ? b.color : 'white',
                  color: active ? 'white' : b.color,
                  borderColor: b.color,
                }}
                data-testid={`crm-aging-chip-${b.key}`}
                title={`Click to ${active ? 'clear' : 'filter by'} ${b.label} overdue bucket`}
              >
                <span className="font-bold">{b.data.n}</span> {'\u00B7'} {b.label} {'\u00B7'} <span className="font-semibold">{fmtCr(b.data.amt)}</span>
              </button>
            );
          })}
          {agingBucket && (
            <button
              onClick={() => setAgingBucket(null)}
              className="px-3 py-1.5 rounded-full text-xs border"
              style={{ background: '#FBF9F6', color: '#5C6B61', borderColor: '#E2DFD8' }}
              data-testid="crm-aging-clear-btn"
            >
              Clear filter
            </button>
          )}
          {/* Contextual bulk WhatsApp button — visible when an aging bucket
              OR the overdueOnly toggle is active, so the operator can fire
              the bulk reminder against the currently-filtered subset
              ("Send WA to all >30d overdue" in one click). The bulk action
              respects all visible filters (overdueOnly, agingBucket, search). */}
          {(agingBucket || overdueOnly) && (() => {
            const contextCount = rows.filter(r => {
              if (!(r.outstanding_total > 0)) return false;
              if (!r.phone_number) return false;
              if (overdueOnly && !r.is_overdue) return false;
              if (agingBucket && bucketOf(r.oldest_od_days || 0) !== agingBucket) return false;
              return true;
            }).length;
            if (contextCount === 0) return null;
            return (
              <button
                onClick={() => openBulkReminders()}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: '#25D366', color: 'white' }}
                title={`Send WhatsApp reminders to all ${contextCount} dealer(s) in the current filter`}
                data-testid="crm-aging-bulk-wa-btn"
              >
                <MessageCircle className="w-3 h-3" /> WhatsApp Filtered ({contextCount})
              </button>
            );
          })()}
        </div>
      )}

      {/* Mark filter strip — narrows rows to a specific call-mark */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="crm-mark-filter-strip">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#5C6B61' }}>Mark:</span>
        {(() => {
          const counts = { any: rows.length };
          CALL_MARKS.forEach(cm => { counts[cm.key] = 0; });
          rows.forEach(r => { const k = r.crm_call_mark || 'none'; counts[k] = (counts[k] || 0) + 1; });
          return (
            <>
              <button
                type="button"
                onClick={() => setMarkFilter([])}
                className="text-xs px-3 py-1 rounded-full font-medium border transition-colors"
                style={markFilter.length === 0
                  ? { background: '#1F1F1F', color: 'white', borderColor: '#1F1F1F' }
                  : { background: 'white', color: '#1F1F1F', borderColor: '#E2DFD8' }}
                data-testid="crm-mark-filter-any"
              >
                Any <span className="ml-1 font-bold">{counts.any || 0}</span>
              </button>
              {CALL_MARKS.map(cm => {
                const active = markFilter.includes(cm.key);
                return (
                <button
                  key={cm.key}
                  type="button"
                  onClick={() => setMarkFilter(prev => prev.includes(cm.key) ? prev.filter(k => k !== cm.key) : [...prev, cm.key])}
                  className="text-xs px-3 py-1 rounded-full font-medium border transition-colors"
                  style={active
                    ? { background: cm.bg === 'transparent' ? '#5C6B61' : cm.bg, color: 'white', borderColor: cm.bg === 'transparent' ? '#5C6B61' : cm.bg }
                    : { background: 'white', color: cm.bg === 'transparent' ? '#5C6B61' : cm.bg, borderColor: cm.bg === 'transparent' ? '#B9BEB6' : cm.bg }}
                  data-testid={`crm-mark-filter-${cm.key}`}
                >
                  {cm.label} <span className="ml-1 font-bold">{counts[cm.key] || 0}</span>
                </button>
              );})}
            </>
          );
        })()}
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3">
        <Input placeholder="Search dealer/town/phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-72 bg-white" data-testid="crm-search" />
        <label className="inline-flex items-center gap-2 text-sm" data-testid="overdue-only-toggle">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={() => exportCrm('excel')} disabled={exporting || sorted.length === 0} data-testid="crm-export-excel">
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCrm('pdf')} disabled={exporting || sorted.length === 0} data-testid="crm-export-pdf">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <div className="text-xs" style={{ color: '#5C6B61' }}>{sorted.length} shown</div>
        </div>
      </div>

      {/* Bulk multi-select action bar — reuses Billing Cycle marks */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-lg border" style={{ background: '#FBF9F6', borderColor: '#E2DFD8' }} data-testid="crm-bulk-bar">
          <span className="text-sm font-semibold" style={{ color: '#2B5339' }} data-testid="crm-bulk-count">{selectedIds.size} selected</span>
          <span className="text-xs" style={{ color: '#5C6B61' }}>Mark all as:</span>
          {CALL_MARKS.filter(cm => cm.key !== 'call_on').map(cm => (
            <Button key={cm.key} size="sm" variant="outline" disabled={bulkBusy} onClick={() => bulkSetMark(cm.key)} data-testid={`crm-bulk-mark-${cm.key}`}
              style={cm.bg === 'transparent' ? { color: '#5C6B61', borderColor: '#B9BEB6' } : { color: cm.bg, borderColor: cm.bg }}>
              {cm.label}
            </Button>
          ))}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" disabled={bulkBusy} data-testid="crm-bulk-mark-call_on" style={{ color: '#B08800', borderColor: '#B08800' }}>Call on…</Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start">
              <Input type="date" value={bulkCallOn} onChange={(e) => setBulkCallOn(e.target.value)} data-testid="crm-bulk-callon-date" />
              <Button size="sm" className="w-full mt-2 text-white" style={{ background: '#B08800' }} disabled={!bulkCallOn || bulkBusy} onClick={() => bulkSetMark('call_on', bulkCallOn)} data-testid="crm-bulk-callon-apply">Apply to {selectedIds.size}</Button>
            </PopoverContent>
          </Popover>
          <div className="flex-1" />
          {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#2B5339' }} />}
          <Button size="sm" variant="ghost" onClick={clearSelection} disabled={bulkBusy} data-testid="crm-bulk-clear">Clear</Button>
        </div>
      )}

      <Card className="overflow-auto">
        <table className="w-full text-sm" data-testid="crm-table">
          <thead>
            <tr style={{ background: '#FBF9F6' }}>
              <th className="p-2 w-10 text-center">
                <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" data-testid="crm-select-all" />
              </th>
              <th className="p-2 text-left cursor-pointer" onClick={() => toggleSort('dealer_name')} data-testid="crm-col-dealer">
                Dealer {sortBy === 'dealer_name' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
              </th>
              <th className="p-2 text-left">Mark</th>
              <th className="p-2 text-left">Town</th>
              <th className="p-2 text-left">Mobile</th>
              <th className="p-2 text-left cursor-pointer" onClick={() => toggleSort('last_dispatch')} data-testid="crm-col-last-dispatch">
                Last Dispatch {sortBy === 'last_dispatch' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
              </th>
              <th className="p-2 text-right cursor-pointer" onClick={() => toggleSort('cycle')}>
                Cycle {sortBy === 'cycle' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
              </th>
              <th className="p-2 text-right cursor-pointer" onClick={() => toggleSort('outstanding')} data-testid="crm-col-outstanding">
                Outstanding {sortBy === 'outstanding' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
                {' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleSort('oldest_od_days'); }}
                  className="ml-1 text-[10px] px-1 py-0.5 rounded"
                  style={{
                    background: sortBy === 'oldest_od_days' ? '#C05C35' : 'transparent',
                    color: sortBy === 'oldest_od_days' ? 'white' : '#C05C35',
                    border: `1px solid #C05C35`,
                  }}
                  title="Sort by oldest overdue days"
                  data-testid="crm-col-oldest-od-days"
                >
                  by OD {sortBy === 'oldest_od_days' ? (sortDir === 'asc' ? '↑' : '↓') : 'd'}
                </button>
              </th>
              <th className="p-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="p-8 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center" style={{ color: '#5C6B61' }}>No dealers match.</td></tr>
            ) : visibleSorted.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-[#F5F1E8] border-t" style={{ borderColor: '#E2DFD8', background: selectedIds.has(r.id) ? '#EEF3EC' : undefined }} onClick={() => openDetail(r)} data-testid="crm-row">
                <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} aria-label={`Select ${r.dealer_name}`} data-testid="crm-row-select" />
                </td>
                <td className="p-2 font-medium">{r.dealer_name || '—'}</td>
                <td className="p-2" onClick={(e) => e.stopPropagation()}><MarkChip row={r} /></td>
                <td className="p-2" style={{ color: '#5C6B61' }}>{r.town || '-'}</td>
                <td className="p-2" style={{ color: '#5C6B61' }}>{r.phone_number || '-'}</td>
                <td className="p-2" style={{ color: '#5C6B61' }}>
                  {r.last_dispatch ? fmtDate(r.last_dispatch) : <span style={{ color: '#C05C35' }}>Never</span>}
                  {r.days_since_dispatch != null && <span className="ml-1 text-xs">({r.days_since_dispatch}d)</span>}
                </td>
                <td className="p-2 text-right">{r.billing_cycle_days}d</td>
                <td className="p-2 text-right font-medium">
                  {r.outstanding_total > 0 ? <>{'\u20B9'}{r.outstanding_total.toLocaleString('en-IN')}</> : <span style={{ color: '#5C6B61' }}>-</span>}
                  {r.oldest_od_days > 0 && <div className="text-[10px]" style={{ color: '#C05C35' }}>{r.oldest_od_days}d OD</div>}
                </td>
                <td className="p-2 text-center">
                  <div className="inline-flex items-center gap-1.5">
                    {r.is_overdue ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: '#C05C35', color: 'white' }}>OVERDUE</span>
                    ) : (
                      <span className="text-xs" style={{ color: '#2B5339' }}>OK</span>
                    )}
                    {r.phone_number && r.outstanding_total > 0 && (
                      <button
                        onClick={(e) => sendReminderWA(r, e)}
                        title="Send outstanding statement on WhatsApp"
                        data-testid="crm-wa-reminder-btn"
                        className="inline-flex items-center justify-center w-6 h-6 rounded hover:opacity-80"
                        style={{ background: '#25D366', color: 'white' }}
                      >
                        <MessageCircle className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {crmHasMore && (
              <tr ref={crmLoadMoreRef} data-testid="crm-load-more">
                <td colSpan="9" className="p-3 text-center" style={{ background: '#FBF9F6' }}>
                  <Button variant="outline" size="sm" onClick={crmShowMore} data-testid="crm-show-more-btn">
                    Show more ({crmRemaining} more)
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
        </>
      )}

      {/* Detail dialog — rendered unconditionally so clicks from EITHER the
          Outstanding & Aging table OR the Billing Cycle table open the same
          popup (user spec, 12/Jun/2026). */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="crm-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {detail?.dealer?.name || 'Dealer'} <span className="text-sm font-normal" style={{ color: '#5C6B61' }}>· {detail?.dealer?.town || ''} · {detail?.dealer?.phone_number || ''}</span>
            </DialogTitle>
            {(() => {
              const r = rows.find(x => x.id === detail?.dealer?.id);
              if (!r || !r.phone_number || r.outstanding_total <= 0) return null;
              const amt = Number(r.outstanding_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
              return (
                <div className="flex items-center gap-2 mt-2">
                  {r.is_overdue ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: '#C05C35', color: 'white' }}>OVERDUE</span>
                  ) : (
                    <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: '#2B5339', color: 'white' }}>OUTSTANDING</span>
                  )}
                  <span className="text-xs" style={{ color: '#5C6B61' }}>{'\u20B9'}{amt}{r.oldest_od_days > 0 ? ` \u00B7 ${r.oldest_od_days}d OD` : ''}</span>
                  <Button
                    size="sm"
                    onClick={(e) => sendReminderWA(r, e)}
                    data-testid="crm-detail-wa-reminder-btn"
                    className="ml-auto h-7 text-xs"
                    style={{ background: '#25D366', color: 'white' }}
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Send WhatsApp Reminder
                  </Button>
                </div>
              );
            })()}
          </DialogHeader>
          {detailLoading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-6" /> : detail && (
            <div className="space-y-5">
              {/* Outstanding invoices */}
              <section>
                <h3 className="font-semibold mb-2" style={{ color: '#C05C35' }}>
                  Outstanding Invoices ({(detail.outstanding_invoices || []).length})
                </h3>
                {detail.on_account && (() => {
                  const oa = detail.on_account;
                  const dueSign = oa.due_sign || 'Dr';
                  const isDr = dueSign === 'Dr';
                  return (
                    <div
                      className="flex items-center justify-between gap-3 px-3 py-2 mb-2 rounded text-xs"
                      style={{
                        background: isDr ? '#FFF5F0' : '#F0F8F2',
                        border: `1px solid ${isDr ? '#E89D4F' : '#7BA889'}`,
                      }}
                      data-testid="crm-on-account-row"
                      title={isDr ? 'Customer has an opening Dr balance (owes us)' : 'Customer has an opening Cr balance (advance / we owe them)'}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold"
                          style={{ background: isDr ? '#C05C35' : '#2B5339', color: 'white' }}
                        >
                          {isDr ? 'Dr' : 'Cr'}
                        </span>
                        <span className="font-semibold" style={{ color: isDr ? '#C05C35' : '#2B5339' }}>
                          On Account opening balance
                        </span>
                        <span style={{ color: '#5C6B61' }}>· Net {'\u20B9'}{Number(oa.net || 0).toLocaleString('en-IN')} {oa.net_sign || 'Dr'}</span>
                      </div>
                      <span className="font-bold tabular-nums" style={{ color: isDr ? '#C05C35' : '#2B5339' }} data-testid="crm-on-account-amount">
                        {isDr ? '' : '−'}{'\u20B9'}{Number(oa.due || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  );
                })()}
                {(detail.outstanding_invoices || []).length === 0 ? (
                  <p className="text-sm" style={{ color: '#5C6B61' }}>No outstanding bills on file.</p>
                ) : (
                  <table className="w-full text-xs border">
                    <thead style={{ background: '#FBF9F6' }}>
                      <tr>
                        <th className="p-2 text-left">Date</th>
                        <th className="p-2 text-left">Txn No</th>
                        <th className="p-2 text-right">Net (₹)</th>
                        <th className="p-2 text-right">Due (₹)</th>
                        <th className="p-2 text-right">OD Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...detail.outstanding_invoices].sort((a, b) => b.od_days - a.od_days).map((inv, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                          <td className="p-2">{inv.date}</td>
                          <td className="p-2">{inv.txn_no}</td>
                          <td className="p-2 text-right">{inv.net.toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right font-medium" style={{ color: '#C05C35' }}>{inv.due.toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right">{inv.od_days}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {/* Dispatches */}
              <section>
                <h3 className="font-semibold mb-2" style={{ color: '#2B5339' }}>Dispatches ({sortedDispatches.length})</h3>
                {sortedDispatches.length === 0 ? (
                  <p className="text-sm" style={{ color: '#5C6B61' }}>No dispatched/delivered orders.</p>
                ) : (
                  <table className="w-full text-xs border" data-testid="crm-dispatches-table">
                    <thead style={{ background: '#FBF9F6' }}>
                      <tr>
                        <th className="p-2 text-left cursor-pointer" onClick={() => setDetailSortDir(detailSortDir === 'asc' ? 'desc' : 'asc')} data-testid="crm-detail-sort-date">
                          Dispatch Date {detailSortDir === 'asc' ? '↑' : '↓'}
                        </th>
                        <th className="p-2 text-left">Items</th>
                        <th className="p-2 text-right">Qty</th>
                        <th className="p-2 text-right">Net Amt</th>
                        <th className="p-2 text-left">Vehicle</th>
                        <th className="p-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDispatches.map((o) => {
                        const dispDate = dispatchDateOf(o);
                        return (
                        <tr key={o.id} className="border-t hover:bg-[#FBF9F6] transition-colors" style={{ borderColor: '#E2DFD8' }}>
                          <td className="p-2">{dispDate ? fmtDate(dispDate) : '-'}</td>
                          <td className="p-2">
                            <button
                              type="button"
                              onClick={() => setReviewOrder(o)}
                              className="text-left hover:underline focus:outline-none"
                              style={{ color: '#2B5339' }}
                              title="Click to review this order"
                              data-testid="crm-tab-dispatch-review-link"
                            >
                              {(o.items || []).map(i => `${i.product_name}×${i.quantity}`).join(', ')}
                            </button>
                          </td>
                          <td className="p-2 text-right">{(o.items || []).reduce((s, i) => s + i.quantity, 0)}</td>
                          <td className="p-2 text-right font-medium" data-testid="crm-tab-dispatch-net-amt">{'\u20B9'}{orderNetAmt(o).toFixed(2)}</td>
                          <td className="p-2">{o.vehicle_number || '-'}</td>
                          <td className="p-2 uppercase">{o.status}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp Reminder queue */}
      <Dialog open={!!bulkQueue} onOpenChange={(o) => { if (!o) setBulkQueue(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="crm-bulk-wa-dialog">
          <DialogHeader>
            <DialogTitle>
              WhatsApp Reminders — {bulkQueue?.items?.length || 0} dealer{(bulkQueue?.items?.length || 0) === 1 ? '' : 's'} with outstanding
            </DialogTitle>
          </DialogHeader>
          {bulkQueue && (() => {
            const sentCount = bulkQueue.sent.size;
            const total = bulkQueue.items.length;
            const pending = bulkQueue.items.filter(r => !bulkQueue.sent.has(r.id));
            const nextRow = pending[0];
            return (
              <div className="space-y-4">
                <div className="text-sm" style={{ color: '#5C6B61' }}>
                  Browsers block auto-opening multiple tabs, so each reminder opens on its own click. Go top-to-bottom — oldest overdue first.
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm">
                    <span className="font-bold" style={{ color: '#2B5339' }}>{sentCount}</span> / {total} sent
                  </div>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#E2DFD8' }}>
                    <div className="h-full transition-all" style={{ width: `${total ? (sentCount / total) * 100 : 0}%`, background: '#25D366' }} />
                  </div>
                  {nextRow && (
                    <Button
                      size="sm"
                      onClick={() => sendBulkItem(nextRow)}
                      data-testid="crm-bulk-wa-send-next-btn"
                      style={{ background: '#25D366', color: 'white' }}
                    >
                      <MessageCircle className="w-3 h-3 mr-1" /> Send Next
                    </Button>
                  )}
                </div>
                <table className="w-full text-sm border" data-testid="crm-bulk-wa-table">
                  <thead style={{ background: '#FBF9F6' }}>
                    <tr>
                      <th className="p-2 text-left">Dealer</th>
                      <th className="p-2 text-right">Outstanding</th>
                      <th className="p-2 text-right">OD</th>
                      <th className="p-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkQueue.items.map((r) => {
                      const done = bulkQueue.sent.has(r.id);
                      return (
                        <tr key={r.id} className="border-t" style={{ borderColor: '#E2DFD8', opacity: done ? 0.55 : 1 }}>
                          <td className="p-2">
                            <div className="font-medium">{r.dealer_name}</div>
                            <div className="text-[10px]" style={{ color: '#5C6B61' }}>{r.town || ''} · {r.phone_number}</div>
                          </td>
                          <td className="p-2 text-right">{'\u20B9'}{Number(r.outstanding_total || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right" style={{ color: '#C05C35' }}>{r.oldest_od_days || 0}d</td>
                          <td className="p-2 text-center">
                            {done ? (
                              <span className="text-xs font-semibold" style={{ color: '#2B5339' }}>✓ Sent</span>
                            ) : (
                              <button
                                onClick={() => sendBulkItem(r)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs hover:opacity-80"
                                style={{ background: '#25D366', color: 'white' }}
                                data-testid="crm-bulk-wa-row-send-btn"
                              >
                                <MessageCircle className="w-3 h-3" /> Send
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => setBulkQueue(null)} data-testid="crm-bulk-wa-close-btn">
                    {sentCount === total && total > 0 ? 'Done' : 'Close'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Review Order — stacked ON TOP of the Dealer Detail dialog. Closing
          this returns the operator to the dispatch list, not all the way out
          (user spec: "always de-escalate one step only"). Edit/Dispatch
          actions are intentionally disabled here — this CRM tab is read-only
          for order review. To edit, the operator should go via Orders tab. */}
      <ReviewOrderDialog
        reviewOrder={reviewOrder}
        onClose={() => setReviewOrder(null)}
        getStatusBadge={(s) => {
          const palette = {
            pending: { bg: '#E89D4F' }, confirmed: { bg: '#2B5339' },
            dispatched: { bg: '#C05C35' }, partial_dispatched: { bg: '#C05C35' },
            delivered: { bg: '#2B5339' },
          };
          const p = palette[s] || palette.pending;
          return <Badge style={{ background: p.bg, color: 'white' }}>{(s || 'pending').toUpperCase()}</Badge>;
        }}
        fmtDate={fmtDate}
        fmtDateTime={(iso) => iso ? new Date(iso).toLocaleString('en-IN') : '-'}
        productDiscountByPid={productDiscountByPid}
        canDispatchOrder={() => false}
        openDispatchDialog={() => {}}
        openEditDialog={() => {}}
        isStrictAdmin={false}
      />
    </div>
  );
};


